import type { K8sdiagFocus } from "@k8s-dashboard/contracts";

/**
 * 라우트 하나를 진단 서비스가 아는 좌표로 옮깁니다. (k8sdiag ADR-0005 §1)
 *
 * 사용자가 타이핑하는 값이 아니라 앱이 이미 아는 값이고, 인가가 아니라
 * 힌트입니다 — 인가 집합 밖이면 상류가 조용히 버립니다.
 *
 * 좌표가 없는 화면은 `null`입니다. 클러스터 전체 화면을 `cluster` 좌표로
 * 보고하지 않습니다: 대조할 네임스페이스가 없어 상류가 어차피 버리고,
 * 화면에는 "이 화면을 근거로 답합니다"가 뜨지 않는데 등록만 남습니다.
 *
 * 네임스페이스는 두 곳에서 옵니다. (#94 · #111)
 * - Namespace 하위 화면(상세·Workload·Pod): **경로 세그먼트**가 신원입니다
 * - 그 밖의 화면: Header의 전역 선택(`scope.namespace`) — URL에 없습니다
 *
 * 어느 쪽도 쿼리에서 읽지 않습니다.
 */
const LABEL = /^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/;

/**
 * 옛 상세 경로(`/workloads/...`·`/pods/...`). 새 경로로 리다이렉트되기를 기다리는
 * 중이라 여기서는 `?ns=`를 아직 지우면 안 됩니다. (#111)
 */
export function isLegacyDetailRoute(pathname: string): boolean {
  return /^\/(workloads|pods)\//.test(pathname);
}

export function routeFocus(
  pathname: string,
  _search: string,
  scope: { clusterId?: string; namespace?: string } = {},
): K8sdiagFocus | null {
  const raw = scope.namespace;
  /* `all`은 "네임스페이스를 고르지 않았다"는 UI 값이지 네임스페이스가 아닙니다. */
  const namespace = raw && raw !== "all" && LABEL.test(raw) ? raw : undefined;
  const cluster = scope.clusterId || undefined;
  const segments = pathname.split("/").filter(Boolean).map(decodeURIComponent);

  const focus = (partial: K8sdiagFocus): K8sdiagFocus => ({
    ...partial,
    ...(partial.namespace ? {} : namespace ? { namespace } : {}),
    ...(cluster ? { cluster_id: cluster } : {}),
  });

  if (segments[0] === "namespaces" && segments[1] && LABEL.test(segments[1])) {
    const ns = segments[1];
    if (segments[2] === "workloads" && segments[3] && segments[4]) {
      return focus({ kind: "workload", workload_kind: segments[3], name: segments[4], namespace: ns });
    }
    if (segments[2] === "pods" && segments[3]) {
      return focus({ kind: "pod", name: segments[3], namespace: ns });
    }
    return focus({ kind: "namespace", namespace: ns });
  }
  return namespace ? focus({ kind: "namespace", namespace }) : null;
}
